import React from 'react';
import './CalendarFilters.scss';
import { User } from '../../types/user';
import { Branch } from '../../types/branch';
import { ReservationType } from '../../types/reservationType';
import { CompactEmployeeSelect } from '../CompactEmployeeSelect/CompactEmployeeSelect';
import { PrimeIcons } from 'primereact/api';
import { useOrganizationContext } from '../../context/OrganizationContext';
import classNames from 'classnames';
import { CompactBranchSelect } from '../CompactBranchSelect/CompactBranchSelect';
import { CompactReservationTypeSelect } from '../CompactReservationTypeSelect/CompactReservationTypeSelect';

interface Props {
  selectedBranchIds: string[];
  setSelectedBranchIds: (ids: string[]) => void;
  selectedEmployees: User[];
  setSelectedEmployees: (employees: User[]) => void;
  selectedReservationTypes: ReservationType[];
  setSelectedReservationTypes: (types: ReservationType[]) => void;
  reservationTypes: ReservationType[];
  hasAppliedFilters: boolean;
  clearFilters: () => void;
}

export const CalendarFilters = ({
  selectedBranchIds,
  setSelectedBranchIds,
  selectedEmployees,
  setSelectedEmployees,
  selectedReservationTypes,
  setSelectedReservationTypes,
  reservationTypes,
  hasAppliedFilters,
  clearFilters,
}: Props) => {
  const { orgBranches } = useOrganizationContext();
  const selectedReservationTypesHasNoAssignedUsers = selectedReservationTypes.some((type) => !type.hasAssignedUsers);

  const getSelectedBranchName = () => {
    if (!selectedBranchIds.length) return 'Sucursales';
    if (selectedBranchIds.length === 1) {
      const branch = orgBranches?.find((b: Branch) => b.id === selectedBranchIds[0]);
      return branch?.name || 'Sucursal';
    }
    return `${selectedBranchIds.length} sucursales`;
  };

  const selectedSingleBranchColor = (() => {
    if (selectedBranchIds.length !== 1) return null;
    const branch = orgBranches?.find((b: Branch) => b.id === selectedBranchIds[0]);
    return branch?.color ?? '#000000';
  })();

  const getSelectedEmployeeName = () => {
    if (!selectedEmployees.length) return 'Empleados';
    if (selectedEmployees.length === 1) {
      return `${selectedEmployees[0].name} ${selectedEmployees[0].surnames}`;
    }
    return `${selectedEmployees.length} empleados`;
  };

  const getSelectedReservationTypeName = () => {
    if (!selectedReservationTypes.length) return 'Reservas';
    if (selectedReservationTypes.length === 1) {
      return selectedReservationTypes[0].name.charAt(0).toUpperCase() + selectedReservationTypes[0].name.slice(1);
    }
    return `${selectedReservationTypes.length} tipos`;
  };

  return (
    <div className='calendar-filters'>
      <div className='filters-container'>
        <div
          className={`filter-label ${hasAppliedFilters ? 'active' : ''}`}
          onClick={hasAppliedFilters ? clearFilters : undefined}
        >
          <i className={hasAppliedFilters ? PrimeIcons.FILTER_SLASH : PrimeIcons.FILTER}></i>
          <span>{hasAppliedFilters ? 'Limpiar' : 'Filtros'}</span>
        </div>
        <div className='filter-buttons'>
          <div className={classNames('filter-item', { 'has-branch-dot': selectedSingleBranchColor !== null })}>
            <i className={classNames('filter-icon', PrimeIcons.BUILDING, { active: selectedBranchIds.length > 0 })}></i>
            {selectedSingleBranchColor !== null && (
              <span className='filter-branch-dot' style={{ backgroundColor: selectedSingleBranchColor }} />
            )}
            <span className={classNames('filter-text', { active: selectedBranchIds.length > 0 })}>{getSelectedBranchName()}</span>
            <CompactBranchSelect
              value={selectedBranchIds}
              onChange={setSelectedBranchIds}
              branches={orgBranches || []}
              className={`filter-dropdown branch-select ${selectedBranchIds.length > 0 ? 'active' : ''}`}
            />
          </div>

          <div className='filter-item'>
            <i className={classNames('filter-icon', PrimeIcons.CALENDAR, { active: selectedReservationTypes.length > 0 })}></i>
            <span className={classNames('filter-text', { active: selectedReservationTypes.length > 0 })}>
              {getSelectedReservationTypeName()}
            </span>
            <CompactReservationTypeSelect
              value={selectedReservationTypes}
              onChange={(selected) => {
                setSelectedReservationTypes(selected);

                if (selected.some((type) => !type.hasAssignedUsers)) {
                  setSelectedEmployees([]);
                }
              }}
              reservationTypes={reservationTypes}
              className={`filter-dropdown type-select ${selectedReservationTypes.length > 0 ? 'active' : ''}`}
            />
          </div>

          {!selectedReservationTypesHasNoAssignedUsers && (
            <div className='filter-item'>
              <i className={classNames('filter-icon', PrimeIcons.USER, { active: selectedEmployees.length > 0 })}></i>
              <span className={classNames('filter-text', { active: selectedEmployees.length > 0 })}>
                {getSelectedEmployeeName()}
              </span>
              <CompactEmployeeSelect
                onChange={setSelectedEmployees}
                value={selectedEmployees}
                className={`filter-dropdown employee-select ${selectedEmployees.length > 0 ? 'active' : ''}`}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
